import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SEOHead from '../components/common/SEOHead';
import { ArrowRight, ArrowLeft, Sparkles, Send } from 'lucide-react';
import PrakritiQuestions from '../data/prakritiQuestions';
import PrakritiResults from '../components/prakriti/PrakritiResults';

const PrakritiAnalysis = () => {
  const [step, setStep] = useState('intro'); // 'intro', 'form', 'questions', 'results'
  const [userInfo, setUserInfo] = useState({ name: '', email: '', phone: '' });
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [scores, setScores] = useState({ vata: 0, pitta: 0, kapha: 0 });

  const questions = PrakritiQuestions;

  const handleUserInfoSubmit = (e) => {
    e.preventDefault();
    if (userInfo.name && userInfo.email && userInfo.phone) {
      setStep('questions');
    }
  };

  const handleAnswer = (questionId, option) => {
    const newAnswers = { ...answers, [questionId]: option };
    setAnswers(newAnswers);

    // Calculate scores based on answer
    const question = questions[currentQuestion];
    const newScores = { ...scores };
    
    if (option.vata) newScores.vata += option.vata;
    if (option.pitta) newScores.pitta += option.pitta;
    if (option.kapha) newScores.kapha += option.kapha;
    
    setScores(newScores);

    // Move to next question after a brief delay
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        // Submit results
        handleSubmitResults(newAnswers, newScores);
      }
    }, 300);
  };

  const handleSubmitResults = async (finalAnswers, finalScores) => {
    // Calculate percentages
    const total = finalScores.vata + finalScores.pitta + finalScores.kapha;
    const percentages = {
      vata: ((finalScores.vata / total) * 100).toFixed(1),
      pitta: ((finalScores.pitta / total) * 100).toFixed(1),
      kapha: ((finalScores.kapha / total) * 100).toFixed(1)
    };

    // Send emails using Hostinger email service
    try {
      await fetch('/api/send-prakriti-results', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userInfo,
          answers: finalAnswers,
          scores: finalScores,
          percentages
        })
      });
    } catch (error) {
      console.error('Error sending results:', error);
    }

    setStep('results');
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  return (
    <>
      <SEOHead
        title="Free Prakriti Analysis - Discover Your Ayurvedic Body Type"
        description="Take our comprehensive Prakriti analysis questionnaire and get personalized insights into your Ayurvedic constitution (Vata, Pitta, Kapha)"
        keywords="prakriti analysis, ayurvedic body type, vata pitta kapha, dosha test"
        canonical="https://www.fertilityayurveda.com/prakriti-analysis"
      />

      <div className="min-h-screen bg-gradient-to-br from-powder-blue/20 via-white to-soft-mint/20 pt-20">
        <AnimatePresence mode="wait">
          {step === 'intro' && (
            <IntroSection onStart={() => setStep('form')} />
          )}

          {step === 'form' && (
            <UserInfoForm 
              userInfo={userInfo}
              setUserInfo={setUserInfo}
              onSubmit={handleUserInfoSubmit}
              onBack={() => setStep('intro')}
            />
          )}

          {step === 'questions' && (
            <QuestionSection
              question={questions[currentQuestion]}
              questionNumber={currentQuestion + 1}
              totalQuestions={questions.length}
              onAnswer={handleAnswer}
              onPrevious={handlePrevious}
              canGoPrevious={currentQuestion > 0}
            />
          )}

          {step === 'results' && (
            <PrakritiResults 
              scores={scores}
              userInfo={userInfo}
            />
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

// Intro Section Component
const IntroSection = ({ onStart }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className="container mx-auto px-4 py-20"
  >
    <div className="max-w-4xl mx-auto text-center">
      <Sparkles className="w-16 h-16 text-primary-teal mx-auto mb-6" />
      <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-6">
        Discover Your <span className="text-primary-teal">Prakriti</span>
      </h1>
      <p className="text-xl text-gray-600 mb-8 leading-relaxed">
        Take our comprehensive Ayurvedic constitution analysis and gain deep insights 
        into your unique body type. Understanding your Prakriti is the first step towards 
        personalized fertility care.
      </p>

      <div className="grid md:grid-cols-3 gap-6 mb-12">
        {[
          { title: '40 Questions', desc: 'Comprehensive assessment of physical and mental traits' },
          { title: '5 Minutes', desc: 'Quick and easy to complete' },
          { title: 'Instant Results', desc: 'Get your Vata-Pitta-Kapha analysis immediately' }
        ].map((item, idx) => (
          <div key={idx} className="bg-white p-6 rounded-2xl shadow-lg">
            <h3 className="text-2xl font-bold text-primary-teal mb-2">{item.title}</h3>
            <p className="text-gray-600">{item.desc}</p>
          </div>
        ))}
      </div>

      <button
        onClick={onStart}
        className="inline-flex items-center space-x-2 bg-primary-teal text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl"
      >
        <span>Start Free Analysis</span>
        <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  </motion.div>
);

// User Info Form Component
const UserInfoForm = ({ userInfo, setUserInfo, onSubmit, onBack }) => (
  <motion.div
    initial={{ opacity: 0, x: 100 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -100 }}
    className="container mx-auto px-4 py-20"
  >
    <div className="max-w-2xl mx-auto">
      <h2 className="text-4xl font-bold text-gray-800 mb-8 text-center">
        Let's Get Started
      </h2>
      <form onSubmit={onSubmit} className="bg-white p-8 rounded-3xl shadow-2xl space-y-6">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Full Name *</label>
          <input
            type="text"
            value={userInfo.name}
            onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-teal focus:outline-none"
            placeholder="Enter your full name"
            required
          />
        </div>

        <div>
          <label className="block text-gray-700 font-semibold mb-2">Email Address *</label>
          <input
            type="email"
            value={userInfo.email}
            onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-teal focus:outline-none"
            placeholder="your.email@example.com"
            required
          />
        </div>

        <div>
          <label className="block text-gray-700 font-semibold mb-2">Phone Number *</label>
          <input
            type="tel"
            value={userInfo.phone}
            onChange={(e) => setUserInfo({ ...userInfo, phone: e.target.value })}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-teal focus:outline-none"
            placeholder="+91 98765 43210"
            required
          />
        </div>

        <div className="flex space-x-4 pt-4">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-full font-semibold hover:bg-gray-50 transition-all"
          >
            Back
          </button>
          <button
            type="submit"
            className="flex-1 bg-primary-teal text-white px-6 py-3 rounded-full font-semibold hover:bg-teal-700 transition-all shadow-lg"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  </motion.div>
);

// Question Section Component
const QuestionSection = ({ question, questionNumber, totalQuestions, onAnswer, onPrevious, canGoPrevious }) => (
  <motion.div
    key={questionNumber}
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9 }}
    className="container mx-auto px-4 py-12"
  >
    <div className="max-w-3xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Question {questionNumber} of {totalQuestions}</span>
          <span>{Math.round((questionNumber / totalQuestions) * 100)}% Complete</span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-primary-teal"
            initial={{ width: 0 }}
            animate={{ width: `${(questionNumber / totalQuestions) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-white p-8 md:p-12 rounded-3xl shadow-2xl">
        <div className="mb-8">
          <span className="inline-block px-4 py-1 bg-primary-teal/10 text-primary-teal rounded-full text-sm font-semibold mb-4">
            {question.category}
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
            {question.question}
          </h2>
        </div>

        {/* Options */}
        <div className="space-y-3">
          {question.options.map((option, idx) => (
            <motion.button
              key={idx}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onAnswer(question.id, option)}
              className="w-full p-4 text-left border-2 border-gray-200 rounded-xl hover:border-primary-teal hover:bg-primary-teal/5 transition-all group"
            >
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 rounded-full border-2 border-gray-300 group-hover:border-primary-teal flex items-center justify-center">
                  <div className="w-3 h-3 rounded-full bg-transparent group-hover:bg-primary-teal"></div>
                </div>
                <span className="text-gray-700 group-hover:text-gray-900 font-medium">
                  {option.text}
                </span>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Previous Button */}
        {canGoPrevious && (
          <button
            onClick={onPrevious}
            className="mt-6 inline-flex items-center space-x-2 text-gray-600 hover:text-primary-teal transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Previous Question</span>
          </button>
        )}
      </div>
    </div>
  </motion.div>
);

export default PrakritiAnalysis;